import React, { useState } from 'react';
import { FormGroup, Label } from 'reactstrap';
import { DatePicker } from 'reactstrap-date-picker'
import { slide as Menu } from 'react-burger-menu';
import { useAppState } from '../AppStateContext'
import { HapticLink as Link, useHaptics } from '../utils/haptics';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseSlateDate, slateHref } from '../utils/slateDate';

import '../styles/sidebar.css';

const Sidebar = () => {
  // The slate's day lives in the URL now (?date=YYYY-MM-DD), so the sidebar reads
  // it from there instead of sharing App state. The sidebar renders outside
  // <Routes>, so it cannot use useParams -- it parses /league/:X itself.
  const location = useLocation();
  const navigate = useNavigate();
  const leagueMatch = /^\/league\/([A-Za-z0-9]+)/.exec(location.pathname);
  const currentLeague = leagueMatch ? leagueMatch[1] : null;
  const urlDate = new URLSearchParams(location.search).get('date');
  // Off a slate there is no URL day to read, so a day picked there is remembered
  // here and carried into whichever league link is clicked next -- the behaviour
  // the shared App state used to give for free.
  const [pickedDate, setPickedDate] = useState(null);
  const activeDate = currentLeague ? (parseSlateDate(urlDate) ? urlDate : null) : pickedDate;
  // Noon UTC of the day: inside the same local calendar day from UTC-12 to UTC+11,
  // which a midnight timestamp is not.
  const pickerValue = activeDate ? `${activeDate}T12:00:00.000Z` : new Date().toISOString();
  const leagueTo = (league) => slateHref(league, activeDate);
  const { isAuthenticated, isTutorialMode, toggleTutorialMode, isDesktopMode, toggleDesktopMode, userTier, userProfile } = useAppState();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleChange = (value) => {
    // The picker hands back an ISO string for noon UTC of the chosen day, so the
    // calendar day is its first ten characters. `new Date(value)` formatted
    // locally is NOT that day east of UTC+11. A cleared picker returns null,
    // which means today.
    const day = typeof value === 'string' ? value.slice(0, 10) : null;
    const valid = parseSlateDate(day) ? day : null;
    if (currentLeague) {
      navigate(slateHref(currentLeague, valid), { replace: true });
    } else {
      setPickedDate(valid);
    }
  };

  const { tap } = useHaptics();

  // Handle menu state changes
  const handleStateChange = (state) => {
    if (state.isOpen) tap();
    setIsMenuOpen(state.isOpen);
  };

  // Close menu function
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <Menu 
      right
      isOpen={isMenuOpen}
      onStateChange={(state) => handleStateChange(state)}
    >
      {/* Indicate user tier */}
      {isAuthenticated && userTier === 'Pro' && (
        <div style={{
          backgroundColor: 'black',
          color: 'white',
          fontSize: '36px',
        }}>
          PRO
        </div>
      )}
      {isAuthenticated && userTier === 'Free' && (
        <div style={{
          backgroundColor: 'black',
          color: 'white',
          fontSize: '36px',
        }}>
          FREE
        </div>
      )}
      {!isAuthenticated && (
        <div style={{
          backgroundColor: 'black',
          color: 'white',
          fontSize: '36px',
        }}>
          ANON
        </div>
      )}
      <hr />
      <Link id="home" className="menu-item" to="/" onClick={closeMenu}>
        Home
      </Link>
      <Link id="about" className="menu-item" to="/about" onClick={closeMenu}>
        About/Contact Us
      </Link>
      <Link id="privacy" className="menu-item" to="/privacypolicy" onClick={closeMenu}>
        Privacy Policy
      </Link>
      <hr />
      {!isAuthenticated ? (
        <>
          <Link id="signin" className="menu-item" to="/signin" onClick={closeMenu}>
            Sign In
          </Link>
          <hr />
          <Link id="register" className="menu-item" to="/register" onClick={closeMenu}>
            Register
          </Link>
        </>
      ) : (
        <>
        <Link id="signout" className="menu-item" to="/signout" onClick={closeMenu}>
          Sign Out
        </Link>
        <hr />
        <Link id="profile" className="menu-item" to="/profile" onClick={closeMenu}>
          Profile
        </Link>  
      </>
      )}
      {isAuthenticated && userProfile?.is_staff && (
        <>
          <hr />
          <Link id="dashboard" className="menu-item" to="/dashboard" onClick={closeMenu}>
            Dashboard
          </Link>
        </>
      )}
      {isAuthenticated && userProfile?.is_staff && (
        <>
          <hr />
          <div>
            <b>Trading</b>
            <Link className="menu-item" to="/trading" onClick={closeMenu} style={{ display: 'block' }}>Dashboard</Link>
            <Link className="menu-item" to="/trading/orders" onClick={closeMenu} style={{ display: 'block' }}>Order Queue</Link>
            <Link className="menu-item" to="/trading/positions" onClick={closeMenu} style={{ display: 'block' }}>Positions</Link>
            <Link className="menu-item" to="/trading/performance" onClick={closeMenu} style={{ display: 'block' }}>Performance</Link>
            <Link className="menu-item" to="/trading/settings" onClick={closeMenu} style={{ display: 'block' }}>Settings</Link>
            <Link className="menu-item" to="/trading/terminal" onClick={closeMenu} style={{ display: 'block' }}>Terminal</Link>
          </div>
        </>
      )}
      <hr />
      <b>American Football</b>
      <Link id="NFL" className="menu-item" to={leagueTo('NFL')} onClick={closeMenu}>
        NFL
      </Link>
      <Link id="NCAAF" className="menu-item" to={leagueTo('NCAAF')} onClick={closeMenu}>
        NCAAF
      </Link>
      <hr />
      <b>Basketball</b>
      <Link id="NBA" className="menu-item" to={leagueTo('NBA')} onClick={closeMenu}>
        NBA
      </Link>
      <Link id="NCAAB" className="menu-item" to={leagueTo('NCAAB')} onClick={closeMenu}>
        NCAAB
      </Link>
      <hr />
      {/* EPL went active on 2026-08-14 — ten seasons, 3,800 fixtures, 34 clubs,
          odds backfilled, daemon polling hourly — and shipped on 2026-08-20, so
          the reason this block was commented out no longer holds.

          MLS is linked ahead of its own onboarding and will read "No games
          scheduled" until it gets one: its data stops in 2024. Brian's call. */}
      <b>Soccer</b>
      <Link id="EPL" className="menu-item" to={leagueTo('EPL')} onClick={closeMenu}>
        EPL
      </Link>
      <Link id="MLS" className="menu-item" to={leagueTo('MLS')} onClick={closeMenu}>
        MLS
      </Link>
      <hr />
      <b>Hockey</b>
      <Link id="NHL" className="menu-item" to={leagueTo('NHL')} onClick={closeMenu}>
        NHL
      </Link>
            <hr />
      <b>Baseball</b>
      <Link id="MLB" className="menu-item" to={leagueTo('MLB')} onClick={closeMenu}>
        MLB
      </Link>
            <hr />

      <b>Rugby League</b>
      <Link id="NRL" className="menu-item" to={leagueTo('NRL')} onClick={closeMenu}>
        NRL
      </Link>
            <hr />

      <b>Aussie Rules Football</b>
      <Link id="AFL" className="menu-item" to={leagueTo('AFL')} onClick={closeMenu}>
        AFL
      </Link>

      <hr />
      <FormGroup check>
        <Label check>
          <input
            type="checkbox"
            checked={isTutorialMode}
            onChange={toggleTutorialMode}
          />{" "}
          Tutorial Mode
        </Label>
      </FormGroup>
      <FormGroup check>
        <Label check>
          <input
            type="checkbox"
            checked={isDesktopMode}
            onChange={toggleDesktopMode}
          />{" "}
          Desktop Mode
        </Label>
      </FormGroup>
      <FormGroup>
        <Label>Select Date</Label>
        <DatePicker
          id="example-datepicker"
          value={pickerValue}
          onChange={(v) => handleChange(v)}
          style={{ fontSize: "16px" }}
        />
      </FormGroup>
    </Menu>
  );
};
  
export default Sidebar;